// Google Maps simplificado - Solo punto principal con transición al plano detallado
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { GoogleMapsLoader } from './GoogleMapsLoader'

interface SimplifiedMapProps {
  className?: string
  height?: string
  onCampingClick?: () => void // Callback para abrir plano detallado
  center?: {
    lat: number
    lng: number
  }
}

const SimplifiedGoogleMap: React.FC<SimplifiedMapProps> = ({
  className = '',
  height = '500px',
  onCampingClick,
  center = {
    lat: -41.308838,
    lng: -72.882037
  }
}) => {
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mapReady, setMapReady] = useState(false)

  // Crear marcador del camping principal usando AdvancedMarkerElement
  const createCampingMarker = useCallback((position: any) => {
    try {
      // Intentar usar AdvancedMarkerElement primero
      if (window.google?.maps?.marker?.AdvancedMarkerElement) {
        // Crear contenido HTML personalizado más elaborado
        const markerContent = document.createElement('div')
        markerContent.innerHTML = `
          <div style="
            width: 48px;
            height: 48px;
            background: radial-gradient(circle, #22c55e 0%, #1e6537 70%);
            border: 3px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: bold;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3), 0 0 0 4px rgba(30,101,55,0.3);
            animation: pulse 2s infinite;
            cursor: pointer;
          ">
            🏕️
          </div>
          <style>
            @keyframes pulse {
              0% { box-shadow: 0 4px 8px rgba(0,0,0,0.3), 0 0 0 0 rgba(30,101,55,0.5); }
              70% { box-shadow: 0 4px 8px rgba(0,0,0,0.3), 0 0 0 10px rgba(30,101,55,0); }
              100% { box-shadow: 0 4px 8px rgba(0,0,0,0.3), 0 0 0 0 rgba(30,101,55,0); }
            }
          </style>
        `

        return new window.google.maps.marker.AdvancedMarkerElement({
          position,
          content: markerContent,
          title: 'Haz click para ver el plano detallado'
        })
      }

      // Fallback a Marker clásico
      if (window.google?.maps?.Marker) {
        return new window.google.maps.Marker({
          position,
          title: 'Haz click para ver el plano detallado',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="22" fill="#1e6537" stroke="white" stroke-width="3" opacity="0.9"/>
                <circle cx="24" cy="24" r="16" fill="#22c55e" opacity="0.8"/>
                <text x="24" y="30" text-anchor="middle" fill="white" font-size="16" font-weight="bold">🏕️</text>
                <circle cx="24" cy="24" r="28" fill="none" stroke="#1e6537" stroke-width="2" opacity="0.3" stroke-dasharray="4,4"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(48, 48),
            anchor: new window.google.maps.Point(24, 24)
          },
          animation: window.google.maps.Animation.DROP
        })
      }

      throw new Error('Ni AdvancedMarkerElement ni Marker están disponibles')
    } catch (error) {
      console.error('Error creando marcador:', error)
      throw error
    }
  }, [])

  // Inicializar mapa simplificado
  const initializeMap = useCallback(async () => {
    if (!mapRef.current || mapInstanceRef.current) return

    try {
      setIsLoading(true)
      setError(null)

      const loader = GoogleMapsLoader.getInstance()
      await loader.load()
      
      if (!window.google?.maps?.Map) {
        throw new Error('Google Maps API no está disponible')
      }

      // Crear mapa centrado en el camping con mapId para AdvancedMarkerElement
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        zoom: 15, // Zoom que muestra el contexto del lago
        center: center,
        mapTypeId: 'satellite',
        mapId: 'CAMPING_OVERVIEW_MAP', // Requerido para AdvancedMarkerElement

        // Controles mínimos
        zoomControl: true,
        mapTypeControl: false, // Ocultar controles Map/Satellite
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false, // Ocultar botón de pantalla completa
        gestureHandling: 'auto'
      })

      mapInstanceRef.current = mapInstance

      // Marcador principal del camping con animación
      const campingMarker = createCampingMarker(center)
      campingMarker.setMap(mapInstance)

      // InfoWindow para hover
      const hoverInfoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; font-family: system-ui, sans-serif; text-align: center;">
            <p style="color: #1e6537; margin: 0; font-size: 13px; font-weight: 500;">
              Haz click para ver el plano detallado
            </p>
          </div>
        `,
        maxWidth: 200
      })

      // Mostrar InfoWindow en hover (solo desktop)
      campingMarker.addListener('mouseover', () => {
        hoverInfoWindow.open(mapInstance, campingMarker)
      })

      // Ocultar InfoWindow cuando sale el mouse (solo desktop)
      campingMarker.addListener('mouseout', () => {
        hoverInfoWindow.close()
      })

      // Click en marcador va directo al plano detallado
      campingMarker.addListener('click', () => {
        hoverInfoWindow.close()
        onCampingClick?.()
      })

      // En dispositivos móviles, mostrar el InfoWindow automáticamente por 3 segundos
      if (deviceInfo.isMobile) {
        setTimeout(() => {
          hoverInfoWindow.open(mapInstance, campingMarker)
          setTimeout(() => {
            hoverInfoWindow.close()
          }, 3000)
        }, 500)
      }

      // Puntos de interés contextuales (volcanes)
      const puntosInteres = [
        {
          nombre: 'Volcán Osorno',
          posicion: { lat: -41.1, lng: -72.493 },
          descripcion: 'Volcán activo con vistas espectaculares'
        },
        {
          nombre: 'Volcán Calbuco', 
          posicion: { lat: -41.326, lng: -72.614 },
          descripcion: 'Volcán activo, última erupción 2015'
        },
        {
          nombre: 'Puerto Varas',
          posicion: { lat: -41.319, lng: -72.983 },
          descripcion: 'Ciudad turística principal'
        }
      ]

      // Agregar marcadores de contexto usando AdvancedMarkerElement o fallback
      puntosInteres.forEach((punto) => {
        let marker

        // Intentar usar AdvancedMarkerElement para marcadores de contexto
        if (window.google?.maps?.marker?.AdvancedMarkerElement) {
          const contextMarkerContent = document.createElement('div')
          contextMarkerContent.innerHTML = `
            <div style="
              width: 24px;
              height: 24px;
              background-color: #a97c4a;
              border: 2px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 10px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              opacity: 0.8;
            ">
              🏔️
            </div>
          `

          marker = new window.google.maps.marker.AdvancedMarkerElement({
            position: punto.posicion,
            map: mapInstance,
            content: contextMarkerContent,
            title: punto.nombre
          })
        } else {
          // Fallback a Marker clásico
          marker = new window.google.maps.Marker({
            position: punto.posicion,
            map: mapInstance,
            title: punto.nombre,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#a97c4a" stroke="white" stroke-width="2" opacity="0.8"/>
                  <text x="12" y="16" text-anchor="middle" fill="white" font-size="10">🏔️</text>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(24, 24)
            }
          })
        }

        const pointInfoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; font-family: system-ui, sans-serif;">
              <h4 style="color: #a97c4a; font-size: 14px; font-weight: 600; margin: 0 0 4px 0;">🏔️ ${punto.nombre}</h4>
              <p style="color: #6b7280; font-size: 12px; margin: 0;">${punto.descripcion}</p>
            </div>
          `,
          maxWidth: 200
        })

        marker.addListener('click', () => {
          pointInfoWindow.open(mapInstance, marker)
        })
      })

      setMapReady(true)
      setIsLoading(false)

    } catch (error) {
      console.error('Error inicializando mapa:', error)
      setError(error instanceof Error ? error.message : 'Error desconocido')
      setIsLoading(false)
    }
  }, [center, createCampingMarker, onCampingClick, deviceInfo.isMobile])

  // Effect principal
  useEffect(() => {
    let mounted = true

    const setupMap = async () => {
      if (mounted && !mapInstanceRef.current) {
        await initializeMap()
      }
    }

    const timer = setTimeout(setupMap, 100)
    return () => {
      mounted = false
      clearTimeout(timer)
    }
  }, [initializeMap])

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        try {
          window.google?.maps?.event?.clearInstanceListeners?.(mapInstanceRef.current)
        } catch (e) {
          // Cleanup error
        }
        mapInstanceRef.current = null
      }
    }
  }, [])

  if (error) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 rounded-xl border-2 border-red-200`} style={{ height }}>
        <div className="text-center p-8 max-w-md">
          <div className="text-red-500 text-5xl mb-4">🗺️</div>
          <h3 className="text-red-600 font-bold text-lg mb-2">Error cargando mapa</h3>
          <p className="text-gray-600 text-sm mb-4">{error}</p>
          <button 
            onClick={() => {
              setError(null)
              mapInstanceRef.current = null
              initializeMap()
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            🔄 Reintentar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative ${className} overflow-hidden rounded-xl`} style={{ height }}>
      <div 
        ref={mapRef} 
        className="w-full h-full"
        style={{ minHeight: height }}
      />
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-95 backdrop-blur-sm">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-camping-200 border-t-camping-600 rounded-full mx-auto mb-3"></div>
            <p className="text-camping-600 font-medium text-sm">Cargando ubicación del camping...</p>
            <p className="text-camping-400 text-xs mt-1">Lago Llanquihue</p>
          </div>
        </div>
      )}


      {/* Botón directo para plano detallado */}
      {mapReady && !isLoading && !error && (
        <button
          onClick={onCampingClick}
          style={{
            backgroundColor: colors.primary,
            color: '#FFFFFF'
          }}
          className={`absolute top-4 right-4 rounded-lg shadow-lg hover:opacity-90 transition-all duration-200 font-medium flex items-center justify-center hover:scale-105 ${
            deviceInfo.isMobile
              ? 'w-10 h-10 text-xl p-0'
              : 'px-4 py-2 text-sm space-x-2'
          }`}
          title={deviceInfo.isMobile ? 'Ver Plano Detallado' : undefined}
        >
          <span>🗺️</span>
          {!deviceInfo.isMobile && <span>Ver Plano Detallado</span>}
        </button>
      )}
    </div>
  )
}

export default SimplifiedGoogleMap
